import { SimpleResponse } from 'Types/simpleResponse'
import { RequestParams, ResponseCatalog, PartnerFullModel } from 'Models'
import { urls } from 'Constants/Urls'
import { axiosDS } from '@pp/axios'
import {
	BankGuarantee,
	BankGuaranteeBuild,
	BankGuaranteeDeliveryData,
	BankGuaranteeFull,
	BgDict,
	BgMetaModel,
	Calendar,
	ChangeSocketBg,
	CreateBg,
	DependentFieldsBg,
	OrderSource,
	BankGuaranteeSaveModel,
	CreateBuildBg,
	SendReleasedDocsParams,
	BgPublicationModel,
	CreateBgPublicationModel,
	BgHistoryModel,
} from 'Pages/ibro'

export const bgRequestApi = {
	getItems: async (params: RequestParams) => {
		const res = await axiosDS().get<ResponseCatalog<BankGuarantee> & { meta: BgMetaModel }>(
			urls.broker.index,
			{ params }
		)
		return res.data
	},

	getBg: async (id: number) => {
		const res = await axiosDS().get<BankGuaranteeFull>(`${urls.broker.index}/${id}`)
		return res.data
	},

	createBg: async (data: CreateBg) => {
		data.source = OrderSource.NEO
		const res = await axiosDS().post<BankGuaranteeFull | BankGuaranteeFull[]>(urls.broker.index, {
			data,
		})
		return res.data
	},

	updateBg: async (data: BankGuaranteeSaveModel) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(`${urls.broker.index}/${data.id}`, {
			data,
		})
		return res.data
	},

	checkLimit: async (data: BankGuaranteeSaveModel) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(
			urls.broker.check_limit.replace(':id', data.id.toString()),
			{ data }
		)
		return res.data
	},

	checkStatus: async (id: number) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(
			urls.broker.check_status.replace(':id', id.toString())
		)
		return res.data
	},

	createCommercialOrder: async (id: number) => {
		const res = await axiosDS().post<BankGuaranteeFull>(
			urls.broker.create_commercial_order.replace(':id', id.toString())
		)
		return res.data
	},

	postRequest: async (id: number) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(
			urls.broker.post_request.replace(':id', id.toString())
		)
		return res.data
	},

	updateRequest: async (id: number, params: Record<string, unknown>) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(
			urls.broker.update_request.replace(':id', id.toString()),
			{ ...params }
		)
		return res.data
	},

	getDependentFields: async () => {
		const res = await axiosDS().get<DependentFieldsBg>(urls.broker.dependent_fields)
		return res.data
	},

	getDict: async () => {
		const res = await axiosDS().get<BgDict>(urls.broker.dict)
		return res.data
	},

	build: async (data: CreateBuildBg) => {
		data.source = OrderSource.NEO
		const res = await axiosDS().post<BankGuaranteeBuild>(urls.broker.build, { data })
		return res.data
	},

	getCalculateCommission: async (data: ChangeSocketBg) => {
		const { request_id: id, ...params } = data
		const url = id
			? urls.broker.calculate_commission.replace(':id', id.toString())
			: urls.broker.commission
		const res = await axiosDS().get<BankGuaranteeFull>(url, { params })
		return res.data
	},

	getBankData: async (id: number, params: ChangeSocketBg) => {
		const res = await axiosDS().get<BankGuaranteeFull>(
			urls.broker.get_bank_data.replace(':id', id.toString()),
			{
				params,
			}
		)
		return res.data
	},

	changeSendAddress: async (id: number, data: BankGuaranteeDeliveryData) => {
		const res = await axiosDS().patch<BankGuaranteeFull>(
			urls.broker.save_new_send_address.replace(':id', id.toString()),
			data
		)
		return res.data
	},

	getCalendarList: async (params: RequestParams) => {
		const res = await axiosDS().get<ResponseCatalog<Calendar>>(urls.broker.calendar, {
			params,
		})
		return res.data
	},

	updateCurrentElements: async (id: number) => {
		const res = await axiosDS().post<BankGuaranteeFull>(
			urls.broker.current_elements.replace(':id', id.toString())
		)
		return res.data
	},

	rework: async (id: number) => {
		const res = await axiosDS().post<BankGuaranteeFull>(
			urls.broker.rework.replace(':id', id.toString())
		)
		return res.data
	},

	addFavorite: async (id: number) => {
		const res = await axiosDS().post<SimpleResponse>(
			urls.broker.favorite.replace(':id', id.toString())
		)
		return res.data
	},

	deleteFavorite: async (id: number) => {
		const res = await axiosDS().delete<SimpleResponse>(
			urls.broker.favorite.replace(':id', id.toString())
		)
		return res.data
	},

	assignRequest: async (
		id: number,
		partner_id: number,
		created_by_user_id: number,
		clear_data: boolean
	) => {
		const res = await axiosDS().post<BankGuaranteeFull>(
			urls.broker.assign_request.replace(':id', id.toString()),
			{ partner_id, created_by_user_id, clear_data }
		)
		return res.data
	},

	svetoforCheck: async (inn: string) => {
		const res = await axiosDS().get<string[]>(urls.broker.svetofor_check.replace(':inn', inn))
		return res.data
	},

	partnerRequest: async (partner_id: string) => {
		const res = await axiosDS().get<PartnerFullModel>(
			urls.pp.partner.show.replace(':id', partner_id)
		)
		return res.data
	},

	sendReleaseDocs: async (id: number, params: SendReleasedDocsParams) => {
		const res = await axiosDS().get(urls.broker.send_released_docs.replace(':id', id.toString()), {
			params,
		})
		return res.data
	},

	createPublication: async (data: CreateBgPublicationModel) => {
		const res = await axiosDS().post<BgPublicationModel>(urls.broker.publications.index, { data })
		return res.data
	},

	updatePublication: async (id: number, data: CreateBgPublicationModel) => {
		const res = await axiosDS().patch<BgPublicationModel>(
			`${urls.broker.publications.index}/${id}`,
			{ data }
		)
		return res.data
	},
	deletePublication: async (id: number) => {
		const res = await axiosDS().delete<SimpleResponse>(`${urls.broker.publications.index}/${id}`)
		return res.data
	},

	getHistory: async (request_id: number) => {
		const res = await axiosDS().get<ResponseCatalog<BgHistoryModel>>(
			urls.broker.history.replace(':request_id', request_id.toString())
		)
		return res.data
	},
}
